import type { ActionTypeEntity } from '@/domains/actionType/actionType.entity';
import {
  Button,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { useCallback, useState } from 'react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { useActionTypeData } from '../hooks/useActionTypeData.hook';
import type { ActionTypeFormData } from '../models/actionType.model';

interface ActionTypeManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ActionTypeManagementModal = ({
  isOpen,
  onClose,
}: ActionTypeManagementModalProps) => {
  const {
    actionTypes,
    loading,
    error,
    createActionType,
    updateActionType,
    deleteActionType,
  } = useActionTypeData();

  const [formData, setFormData] = useState<ActionTypeFormData>({
    desc_tipo_accion: '',
    personal: false,
  });

  const [editingId, setEditingId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setFormData({ desc_tipo_accion: '', personal: false });
    setEditingId(null);
  };

  const handleEdit = (actionType: ActionTypeEntity) => {
    setFormData({
      desc_tipo_accion: actionType.desc_tipo_accion,
      personal: actionType.personal,
    });
    setEditingId(actionType.cod_tipo_accion);
  };

  const handleDelete = async (cod_tipo_accion: number) => {
    if (window.confirm('¿Está seguro de eliminar este tipo de acción?')) {
      setIsSubmitting(true);
      await deleteActionType(cod_tipo_accion);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.desc_tipo_accion.trim()) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      desc_tipo_accion: formData.desc_tipo_accion.trim(),
      personal: formData.personal,
    };

    let success = false;

    if (editingId !== null) {
      success = await updateActionType(editingId, payload);
    } else {
      success = await createActionType(payload);
    }

    if (success) {
      resetForm();
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetForm();
  };

  const renderCell = useCallback(
    (actionType: ActionTypeEntity, columnKey: React.Key) => {
      switch (columnKey) {
        case 'cod_tipo_accion':
          return <span className="text-sm">{actionType.cod_tipo_accion}</span>;
        case 'desc_tipo_accion':
          return (
            <span className="text-sm font-medium">
              {actionType.desc_tipo_accion}
            </span>
          );
        case 'personal':
          return (
            <Checkbox isSelected={actionType.personal} isDisabled size="sm" />
          );
        case 'actions':
          return (
            <div className="flex gap-2">
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="primary"
                onPress={() => handleEdit(actionType)}
                isDisabled={isSubmitting}
              >
                <FiEdit2 size={16} />
              </Button>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                color="danger"
                onPress={() => handleDelete(actionType.cod_tipo_accion)}
                isDisabled={isSubmitting}
              >
                <FiTrash2 size={16} />
              </Button>
            </div>
          );
        default:
          return null;
      }
    },
    [isSubmitting],
  );

  const columns = [
    { key: 'cod_tipo_accion', label: 'Código' },
    { key: 'desc_tipo_accion', label: 'Descripción' },
    { key: 'personal', label: 'Personal' },
    { key: 'actions', label: 'Acciones' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-xl font-bold text-primary-pink/80">
            Gestión de Tipos de Acciones
          </h3>
        </ModalHeader>

        <ModalBody className="pb-6">
          <div className="flex flex-col gap-6">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  {editingId !== null
                    ? 'Editar Tipo de Acción'
                    : 'Registrar Nueva Acción'}
                </h4>

                <div className="flex flex-col gap-3">
                  <Input
                    label="Descripción de la Acción"
                    placeholder="Ingrese la descripción"
                    value={formData.desc_tipo_accion}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        desc_tipo_accion: value,
                      }))
                    }
                    isRequired
                    variant="bordered"
                    size="sm"
                  />

                  <Checkbox
                    isSelected={formData.personal}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, personal: value }))
                    }
                    size="sm"
                  >
                    Es una acción personal
                  </Checkbox>

                  <div className="flex gap-2 justify-end">
                    {editingId !== null && (
                      <Button
                        size="sm"
                        variant="light"
                        onPress={handleCancel}
                        isDisabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                    )}
                    <Button
                      type="submit"
                      color="primary"
                      size="sm"
                      isLoading={isSubmitting}
                      isDisabled={!formData.desc_tipo_accion.trim()}
                    >
                      {editingId !== null ? 'Actualizar' : 'Registrar'}
                    </Button>
                  </div>
                </div>
              </div>
            </form>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">
                Tipos de Acciones Registrados
              </h4>

              {loading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" color="primary" />
                </div>
              ) : error ? (
                <div className="text-center py-4 text-danger">{error}</div>
              ) : (
                <Table
                  aria-label="Tabla de tipos de acciones"
                  removeWrapper
                  classNames={{
                    th: 'bg-primary-pink/10 text-primary-pink/80',
                  }}
                >
                  <TableHeader columns={columns}>
                    {(column) => (
                      <TableColumn key={column.key}>{column.label}</TableColumn>
                    )}
                  </TableHeader>
                  <TableBody
                    items={actionTypes}
                    emptyContent="No hay tipos de acciones registrados"
                  >
                    {(item) => (
                      <TableRow key={item.cod_tipo_accion}>
                        {(columnKey) => (
                          <TableCell>{renderCell(item, columnKey)}</TableCell>
                        )}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ActionTypeManagementModal;
